import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Project, DiversSubProject } from '../types';
import { getISOYear } from '../utils/overtimeUtils';
import { getMonday, dateKey, generateWeekDays, getWeekNumber } from '../utils/dateUtils';
import { getFrenchHolidays } from '../utils/holidayUtils';
import type {
  LeaveRange,
  GridRow,
  DailyIndemnity,
  Ventilation,
  TimeEntryJoined,
} from '../pages/timesheet/types';
import type { DayStatuses } from '../utils/sheetStatusUtils';

export function useTimesheetData(
  employeeId: string | undefined,
  effectiveUserId: string | undefined,
  isDelegateMode: boolean
) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [rows, setRows] = useState<GridRow[]>([]);
  const [indemnities, setIndemnities] = useState<Record<string, DailyIndemnity>>({});
  const [sheetStatus, setSheetStatus] = useState<string>('draft');
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [approvedLeaves, setApprovedLeaves] = useState<LeaveRange[]>([]);

  // --- SYSTÈME DE MOIS DE SAISIE (pour semaines à cheval) ---
  const [entryMonth, setEntryMonth] = useState<{ year: number; month: number } | null>(null);
  const [showMonthTransition, setShowMonthTransition] = useState(false);

  // --- Jours de formation et ID des projets spéciaux ---
  const [trainingDays, setTrainingDays] = useState<string[]>([]);
  const [trainingProjectId, setTrainingProjectId] = useState<string>('');

  const [ventilations, setVentilations] = useState<Record<string, Ventilation>>({});

  // --- Statut par jour (source de vérité pour le verrouillage) ---
  // 'draft'     : non soumis
  // 'submitted' : soumis par l'employé, en attente admin
  // 'validated' : validé par l'admin (RLS figée si tous les jours sont validated)
  const [dayStatuses, setDayStatuses] = useState<DayStatuses>({});

  // Mois déjà traités (soumis OU validés) — dérivé de dayStatuses.
  // Utilisé pour déterminer l'activeEntryMonth : si un mois est "clos" (submitted
  // ou validated), l'employé doit saisir l'autre mois de la semaine à cheval.
  // Note : le nom 'submittedMonths' est historique (champ DB du même nom), mais
  // sémantiquement on considère maintenant aussi 'validated' comme "traité".
  const submittedMonths = useMemo(() => {
    const months = new Set<string>();
    Object.entries(dayStatuses).forEach(([dateStr, status]) => {
      if (status === 'submitted' || status === 'validated') {
        const d = new Date(dateStr);
        months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months);
  }, [dayStatuses]);

  const [targetEmployeeName, setTargetEmployeeName] = useState<string>('');
  /**
   * Rôle de l'employé TARGET en mode délégué. Permet de calculer correctement
   * isBE et isBE35h selon le PROFIL DU TARGET et non celui de l'admin connecté.
   * Sans cette distinction, un admin saisissant pour un BE ne voyait pas la
   * VentilationModal (et donc ne pouvait pas saisir les ventilations BE
   * requises). Bug détecté pendant l'audit du 23/05/2026.
   */
  const [targetEmployeeRole, setTargetEmployeeRole] = useState<string | null>(null);
  const [customWeeklyHours, setCustomWeeklyHours] = useState<number | null>(null);
  const [overrideWeeklyHours, setOverrideWeeklyHours] = useState<number | null>(null);

  const [loadingData, setLoadingData] = useState(false);
  const [plannedGDDays, setPlannedGDDays] = useState<Set<string>>(new Set());

  const weekDays = useMemo(() => generateWeekDays(currentWeekStart), [currentWeekStart]);
  const holidays = useMemo(() => getFrenchHolidays(getISOYear(currentWeekStart)), [currentWeekStart]);

  // --- SYSTÈME DE MOIS DE SAISIE AVEC SEMAINES À CHEVAL ---

  // Détecte les mois présents dans la semaine
  const weekMonths = useMemo(() => {
    const months: { year: number; month: number; days: number; firstDay: Date }[] = [];
    const monthMap: Record<string, { year: number; month: number; days: number; firstDay: Date }> = {};

    weekDays.forEach(day => {
      const key = `${day.getFullYear()}-${day.getMonth()}`;
      if (!monthMap[key]) {
        monthMap[key] = { year: day.getFullYear(), month: day.getMonth(), days: 0, firstDay: day };
      }
      monthMap[key].days++;
    });

    // Trier par ordre chronologique
    Object.values(monthMap).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    }).forEach(m => months.push(m));

    return months;
  }, [weekDays]);

  // Semaine à cheval = contient 2 mois différents
  const isStraddleWeek = weekMonths.length > 1;

  // Premier mois de la semaine (par défaut pour la saisie)
  const firstMonth = weekMonths[0];
  const secondMonth = weekMonths.length > 1 ? weekMonths[1] : null;

  // Mois de saisie actif : soit l'état, soit le premier mois NON SOUMIS par défaut
  // Corrige le bug où la soumission d'un mois bloquait l'édition du mois suivant
  const activeEntryMonth = useMemo(() => {
    if (entryMonth) {
      const isValidMonth = weekMonths.some(m => m.year === entryMonth.year && m.month === entryMonth.month);
      if (isValidMonth) return entryMonth;
    }
    // Si le premier mois est déjà soumis et qu'il y a un second mois non soumis, pointer vers le second
    if (firstMonth && secondMonth && submittedMonths.includes(
      `${firstMonth.year}-${String(firstMonth.month + 1).padStart(2, '0')}`
    )) {
      const secondKey = `${secondMonth.year}-${String(secondMonth.month + 1).padStart(2, '0')}`;
      if (!submittedMonths.includes(secondKey)) {
        return secondMonth;
      }
    }
    return firstMonth;
  }, [entryMonth, weekMonths, firstMonth, secondMonth, submittedMonths]);

  // Réinitialiser l'entryMonth quand on change de semaine
  useEffect(() => {
    setEntryMonth(null);
    setShowMonthTransition(false);
  }, [currentWeekStart]);

  // Vérifier si un jour est hors du mois de saisie actif
  const isOutsideEntryMonth = useCallback((day: Date) => {
    if (!activeEntryMonth) return false;
    return day.getFullYear() !== activeEntryMonth.year || day.getMonth() !== activeEntryMonth.month;
  }, [activeEntryMonth]);

  // Alias pour compatibilité avec le code existant
  const isOutsideClosureMonth = isOutsideEntryMonth;

  // Déterminer si tous les jours de la semaine sont soumis (au sens strict)
  const allWeekDaysSubmitted = useMemo(() => {
    if (weekDays.length === 0) return false;
    return weekDays.every(day => dayStatuses[dateKey(day)] === 'submitted');
  }, [weekDays, dayStatuses]);

  // Tous les jours sont "actionnés" : soit submitted, soit déjà validated.
  // Plus aucune action n'est possible côté employé.
  // Distinct de allWeekDaysSubmitted qui exclurait les jours déjà validés.
  const allWeekDaysActioned = useMemo(() => {
    if (weekDays.length === 0) return false;
    return weekDays.every(day => {
      const s = dayStatuses[dateKey(day)];
      return s === 'submitted' || s === 'validated';
    });
  }, [weekDays, dayStatuses]);

  // Verrouillage complet : feuille intégralement validée (le trigger DB
  // derived_status garantit que sheetStatus='validated' ↔ tous jours='validated').
  // Sans la propagation 'validated', l'ancien code testait day_statuses pour
  // 'submitted' uniquement → restait false → bouton "Signer et Envoyer" actif
  // sur feuille validée (bug remonté par Virginie).
  const isFullyLocked = sheetStatus === 'validated';
  const isLocked = isFullyLocked || (sheetStatus === 'pending' && allWeekDaysActioned);

  // Verrouillage JOUR PAR JOUR : lit directement dayStatuses
  // Un jour est verrouillé s'il est soumis OU validé (rejeté = on peut re-saisir).
  const isLockedForDay = useCallback((day: Date): boolean => {
    if (isFullyLocked) return true;
    if (sheetStatus === 'rejected') return false;
    const s = dayStatuses[dateKey(day)];
    return s === 'submitted' || s === 'validated';
  }, [dayStatuses, isFullyLocked, sheetStatus]);

  // 1. Initialisation
  useEffect(() => {
    const targetId = isDelegateMode ? employeeId : effectiveUserId;
    if (targetId) {
      supabase.rpc('get_employee_public_info', { p_employee_id: targetId })
        .then(({ data }) => {
          const emp = (data as { full_name: string; role: string; custom_weekly_hours: number | null }[] | null)?.[0];
          if (emp) {
            if (isDelegateMode) {
              setTargetEmployeeName(emp.full_name);
              setTargetEmployeeRole(emp.role);
            }
            setCustomWeeklyHours(emp.custom_weekly_hours ?? null);
          }
        });
    }
    supabase.from('projects').select('id, code, name, zone, category, parent_entity, gd_amount, gd_travel_hours').eq('is_active', true)
      .then(({ data }) => {
        const projs = data as Project[] || [];
        setProjects(projs);

        const trainingProj = projs.find(p => p.code === 'SYS-FORM');
        if (trainingProj) setTrainingProjectId(trainingProj.id);
      });

    supabase.from('divers_sub_projects').select('*').eq('is_active', true)
      .then(({ data }) => {
        if (data) setDiversSubProjects(data as DiversSubProject[]);
      });
  }, [employeeId, isDelegateMode, effectiveUserId]);

  // 2. Chargement des données complètes
  const fetchWeekData = useCallback(async () => {
    if (!effectiveUserId) return;
    setLoadingData(true);
    setRows([]);
    setIndemnities({});
    setVentilations({});
    setApprovedLeaves([]);
    setTrainingDays([]);
    setPlannedGDDays(new Set());
    setSheetStatus('draft');
    setRejectionReason(null);
    setDayStatuses({});
    setOverrideWeeklyHours(null);

    try {
      const year = getISOYear(currentWeekStart);
      const weekNumber = getWeekNumber(currentWeekStart);
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      // A. Récupération congés validés
      const { data: leaves } = await supabase
        .from('leaves')
        .select('start_date, end_date')
        .eq('employee_id', effectiveUserId)
        .eq('status', 'validated')
        .lte('start_date', weekEnd.toISOString())
        .gte('end_date', currentWeekStart.toISOString());

      if (leaves) {
        setApprovedLeaves(leaves.map(l => ({
          start: new Date(l.start_date),
          end: new Date(l.end_date)
        })));
      }

      // B. Récupération jours de formation
      const { data: trains } = await supabase.from('training_days')
        .select('day')
        .eq('employee_id', effectiveUserId)
        .gte('day', currentWeekStart.toISOString())
        .lte('day', weekEnd.toISOString());

      if (trains) {
        setTrainingDays(trains.map(t => t.day));
      }

      // B2. Récupération des jours planifiés en GD (pour warnings)
      const weekStartStr = currentWeekStart.toISOString().slice(0, 10);
      const weekEndStr = weekEnd.toISOString().slice(0, 10);
      const { data: gdAssignments } = await supabase
        .from('project_assignments')
        .select('assignment_date, project:projects!inner(zone)')
        .eq('employee_id', effectiveUserId)
        .gte('assignment_date', weekStartStr)
        .lte('assignment_date', weekEndStr);

      if (gdAssignments) {
        const gdDays = new Set<string>();
        for (const a of gdAssignments) {
          const proj = a.project as unknown as { zone: string | number | null };
          if (proj && String(proj.zone) === 'GD') {
            gdDays.add(a.assignment_date);
          }
        }
        setPlannedGDDays(gdDays);
      }

      // C. Récupération feuille d'heure PRINCIPALE (semaine courante)
      const { data: sheet } = await supabase
        .from('weekly_sheets')
        .select('id, status, rejection_reason, submitted_months, override_weekly_hours, day_statuses')
        .eq('employee_id', effectiveUserId)
        .eq('year', year)
        .eq('week_number', weekNumber)
        .maybeSingle();

      if (sheet) {
        setSheetStatus(sheet.status);
        setRejectionReason(sheet.rejection_reason || null);
        setOverrideWeeklyHours(sheet.override_weekly_hours ?? null);

        // Charger dayStatuses — rétrocompatibilité avec submitted_months
        const dbDayStatuses = (sheet.day_statuses as DayStatuses) || {};
        if (Object.keys(dbDayStatuses).length > 0) {
          setDayStatuses(dbDayStatuses);
        } else if (sheet.submitted_months && sheet.submitted_months.length > 0) {
          // Migration à la volée : construire dayStatuses depuis submitted_months
          const wDays = generateWeekDays(currentWeekStart);
          const migrated: DayStatuses = {};
          wDays.forEach(day => {
            const dKey = dateKey(day);
            const monthKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}`;
            migrated[dKey] = sheet.submitted_months.includes(monthKey) ? 'submitted' : 'draft';
          });
          setDayStatuses(migrated);
        }

        const { data: rawEntries } = await supabase
          .from('time_entries')
          .select(`*, project:projects (code, name, zone, gd_amount, gd_travel_hours), divers_sub_project:divers_sub_projects (id, name, divers_number, zone)`)
          .eq('sheet_id', sheet.id);

        if (rawEntries && rawEntries.length > 0) {
          const entries = rawEntries as unknown as TimeEntryJoined[];
          const newRowsMap: Record<string, GridRow> = {};
          const newIndemnities: Record<string, DailyIndemnity> = {};
          const newVentilations: Record<string, Ventilation> = {};

          const entryIds = entries.map(e => e.id);
          const { data: rawVentilations } = await supabase
            .from('be_ventilations')
            .select('*')
            .in('time_entry_id', entryIds);

          entries.forEach((entry) => {
            if (entry.project?.code === 'SYS-FORM') return;

            // Clé de regroupement des rows : on distingue les sous-projets
            // divers du même parent. Sans cela, EUROLEV et CIRON DAF
            // (tous deux sur 26E0259) seraient fusionnés dans la même row
            // côté employé — perte d'info structurelle.
            const subId = entry.divers_sub_project_id ?? null;
            const isDivers = subId !== null;
            const rowKey = isDivers ? `divers:${subId}` : entry.project_id;
            if (!newRowsMap[rowKey]) {
              // Label du row : pour un sous-projet divers, on garde le format
              // `26E0259 - FIMOR` (code parent + nom client) cohérent avec
              // ProjectSelector + Timesheet.tsx (cf formatDiversLabel).
              const parentCode = entry.project?.code ?? '???';
              const projectName = isDivers && entry.divers_sub_project
                ? `${parentCode} - ${entry.divers_sub_project.name}`
                : (entry.project ? `${parentCode} - ${entry.project.name}` : 'Inconnu');
              newRowsMap[rowKey] = {
                  projectId: rowKey,
                  projectName,
                  hours: {},
                  descriptions: {},
                  zones: {}
              };
            }

            const totalHours = (entry.hours_normal || 0) + (entry.hours_saturday || 0) + (entry.hours_major || 0);

            if (totalHours > 0) {
                newRowsMap[rowKey].hours[entry.work_date] = totalHours;
                if (entry.description) {
                    newRowsMap[rowKey].descriptions[entry.work_date] = entry.description;
                }
                if (entry.travel_zone !== undefined) {
                    newRowsMap[rowKey].zones[entry.work_date] = entry.travel_zone;
                }
            }

            const projectGDAmount = entry.project?.gd_amount;
            const projectGDTravelHours = entry.project?.gd_travel_hours;
            const isGDFromDB = entry.is_grand_deplacement === true;

            const existingInd = newIndemnities[entry.work_date];
            if (!existingInd) {
               newIndemnities[entry.work_date] = {
                 meals: entry.meals_count,
                 transport: isGDFromDB ? 0 : (entry.indemnity_transport || 0),
                 trajet: isGDFromDB ? 0 : (entry.indemnity_trajet || 0),
                 is_grand_deplacement: isGDFromDB,
                 gd_amount: isGDFromDB ? (entry.gd_amount || projectGDAmount) : undefined,
                 gd_travel_hours: isGDFromDB ? (entry.gd_travel_hours || projectGDTravelHours) : undefined
               };
            } else if (isGDFromDB && (entry.gd_amount || projectGDAmount)) {
               existingInd.is_grand_deplacement = true;
               existingInd.transport = 0;
               existingInd.trajet = 0;
               existingInd.gd_amount = entry.gd_amount || projectGDAmount;
               existingInd.gd_travel_hours = entry.gd_travel_hours || projectGDTravelHours;
            }

            if (rawVentilations) {
              const vents = rawVentilations.filter(v => v.time_entry_id === entry.id);
              if (vents.length > 0) {
                const vObj: Ventilation = { etude: 0, suivi: 0, mise_en_service: 0, chantier: 0 };
                vents.forEach(v => {
                   if (v.work_type === 'etude') vObj.etude = v.hours;
                   if (v.work_type === 'suivi') vObj.suivi = v.hours;
                   if (v.work_type === 'mise_en_service') vObj.mise_en_service = v.hours;
                   if (v.work_type === 'chantier') vObj.chantier = v.hours;
                });
                // Clé ventilation : on utilise rowKey (qui inclut le sub_id si divers)
                // pour matcher la convention de useTimesheetActions (ventilationsPayload)
                newVentilations[`${rowKey}_${entry.work_date}`] = vObj;
              }
            }
          });

          setRows(Object.values(newRowsMap));
          setIndemnities(newIndemnities);
          setVentilations(newVentilations);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  }, [currentWeekStart, effectiveUserId]);

  useEffect(() => { fetchWeekData(); }, [fetchWeekData]);

  // Helper pour vérifier si un jour est bloqué (férié ou congé)
  const isDayBlocked = useCallback((d: Date) => {
    const dStr = dateKey(d);
    if (holidays.includes(dStr)) return true;
    if (approvedLeaves.some(l => d >= l.start && d <= l.end)) return true;
    return false;
  }, [holidays, approvedLeaves]);

  const isTraining = useCallback((d: Date) => {
      const dStr = dateKey(d);
      return trainingDays.includes(dStr);
  }, [trainingDays]);

  // Actions - Navigation intelligente pour semaines à cheval
  const changeWeek = useCallback((offset: number) => {
    if (isStraddleWeek && offset > 0) {
      if (activeEntryMonth && secondMonth &&
          activeEntryMonth.year === firstMonth.year &&
          activeEntryMonth.month === firstMonth.month) {
        setShowMonthTransition(true);
        setTimeout(() => {
          setEntryMonth({ year: secondMonth.year, month: secondMonth.month });
          setShowMonthTransition(false);
        }, 150);
        return;
      }
    }

    if (isStraddleWeek && offset < 0) {
      if (activeEntryMonth && secondMonth &&
          activeEntryMonth.year === secondMonth.year &&
          activeEntryMonth.month === secondMonth.month) {
        setShowMonthTransition(true);
        setTimeout(() => {
          setEntryMonth({ year: firstMonth.year, month: firstMonth.month });
          setShowMonthTransition(false);
        }, 150);
        return;
      }
    }

    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (offset * 7));
    setCurrentWeekStart(newDate);
  }, [isStraddleWeek, activeEntryMonth, secondMonth, firstMonth, currentWeekStart]);

  return {
    // Data state
    projects,
    diversSubProjects,
    currentWeekStart,
    rows,
    setRows,
    indemnities,
    setIndemnities,
    sheetStatus,
    setSheetStatus,
    rejectionReason,
    approvedLeaves,
    entryMonth,
    setEntryMonth,
    showMonthTransition,
    setShowMonthTransition,
    trainingDays,
    trainingProjectId,
    ventilations,
    setVentilations,
    dayStatuses,
    setDayStatuses,
    submittedMonths,
    targetEmployeeName,
    targetEmployeeRole,
    customWeeklyHours,
    overrideWeeklyHours,
    setOverrideWeeklyHours,
    loadingData,
    plannedGDDays,

    // Computed values
    weekDays,
    holidays,
    weekMonths,
    isStraddleWeek,
    firstMonth,
    secondMonth,
    activeEntryMonth,
    isOutsideEntryMonth,
    isOutsideClosureMonth,
    isFullyLocked,
    allWeekDaysSubmitted,
    isLocked,
    isLockedForDay,
    isDayBlocked,
    isTraining,

    // Actions
    changeWeek,
    fetchWeekData,
  };
}
